import { motion, AnimatePresence } from "framer-motion";
import { useState } from "react";
import { Type, ArrowLeft, ArrowRight } from "lucide-react";
import { useLightbox } from "./LightboxContext";

const dogsMag = {
  title: "Rasepasa",
  english: "Magazine about dogs",
  category: "Editorial · Independent magazine",
  year: "2025",
  blurb:
    "A made-up magazine project — Rasepasa is a fictional editorial built around dog breeds: where each one comes from, the histories behind the names, and the day-to-day care every pup deserves. Layout and typography crafted in Adobe InDesign.",
  accent: "#8B7E74",
  pages: [
    { src: "/typography/dogs/p00.jpg", label: "Cover", portrait: true },
    ...Array.from({ length: 12 }, (_, i) => ({
      src: `/typography/dogs/p${String(i + 1).padStart(2, "0")}.jpg`,
      label: `Spread · ${String(i + 1).padStart(2, "0")}`,
      portrait: false,
    })),
  ],
};

const featured = {
  title: "Mr. Penumbra's 24-Hour Bookstore",
  titleCyr: "Тајновита књижара господина Пенумбре",
  author: "Robin Sloan",
  publisher: "Vulkan, 2025",
  category: "Book cover · Serbian edition",
  blurb:
    "A speculative re-cover for the Serbian translation of Robin Sloan's novel. The back panel hides the full title inside a grid of Cyrillic letters — a word-search puzzle where only the key letters glow white against muted greys, with the author's name РОБИН СЛОАН punching through in a warm yellow.",
  accent: "#F2C200",
  front: "/typography/penumbra-cover-front.jpg",
  back: "/typography/penumbra-cover-back.jpg",
  spine: "/typography/penumbra-spine.jpg",
};

const mockups = [
  {
    src: "https://customer-assets.emergentagent.com/job_portfolio-pro-1623/artifacts/ix3pmanv_Free%20Hardcover%20Books%20Mockup.jpg",
    label: "Hardcover stack",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_portfolio-pro-1623/artifacts/ghpjmjc4_Free%20Open%20Hardcover%20Book%20Mockup.jpg",
    label: "Open spread",
  },
];

const magazine = {
  title: "Časopis o umetnosti",
  english: "Magazine of Art",
  feature: "Banksy — umetnik bez lica",
  category: "Editorial · Interior spread",
  year: "2025",
  blurb:
    "An editorial spread for an art magazine — a feature piece on Banksy. Layout and typography crafted in Adobe InDesign.",
  accent: "#E63946",
  flat: "/typography/magazine-flat.jpg",
  mockup: "/typography/magazine-mockup.jpg",
};

export default function TypographyWorks() {
  const { open } = useLightbox();

  return (
    <div className="mb-20 md:mb-28" data-testid="typography-works">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10 md:mb-14">
        <div className="flex items-center gap-4">
          <span className="w-10 h-10 border border-[var(--ink)] flex items-center justify-center">
            <Type className="w-4 h-4" strokeWidth={1.5} />
          </span>
          <h3 className="font-sans-display text-3xl md:text-5xl tracking-[-0.02em] font-light uppercase">
            Typography
          </h3>
        </div>
        <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-[var(--ink-soft)]">
          §03.d — Book covers · editorial · letterforms
        </span>
      </div>

      {/* Featured: three-panel jacket spread on a black canvas */}
      <div className="bg-[#0e0e0e] text-white p-6 md:p-10 lg:p-14">
        {/* Tag row */}
        <div className="flex items-center justify-between mb-6 md:mb-8">
          <span
            className="font-mono text-[10px] uppercase tracking-[0.3em]"
            style={{ color: featured.accent }}
          >
            {featured.category}
          </span>
          <span className="hidden md:inline font-mono text-[10px] uppercase tracking-[0.3em] text-white/40">
            Click any panel to expand ↗
          </span>
        </div>

        {/* Jacket spread — Front | Spine | Back, true proportions, flush */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.85 }}
          className="grid w-full shadow-[0_30px_80px_-20px_rgba(242,194,0,0.22)]"
          style={{
            gridTemplateColumns: "964fr 136fr 964fr",
          }}
          data-testid="penumbra-jacket-spread"
        >
          {/* FRONT */}
          <button
            type="button"
            onClick={() => open(featured.front, `${featured.titleCyr} — front cover`)}
            data-testid="typography-front-trigger"
            className="relative block bg-black cursor-zoom-in group focus:outline-none overflow-hidden"
            aria-label="Open front cover"
          >
            <img
              src={featured.front}
              alt={`${featured.titleCyr} — front cover`}
              className="block w-full h-auto transition-transform duration-[1200ms] ease-out group-hover:scale-[1.025]"
              loading="lazy"
            />
            <span
              className="absolute top-4 left-4 font-mono text-[10px] uppercase tracking-[0.3em] text-white/90 bg-black/55 backdrop-blur-sm px-2.5 py-1"
              style={{ borderLeft: `3px solid ${featured.accent}` }}
            >
              Back
            </span>
          </button>

          {/* SPINE */}
          <button
            type="button"
            onClick={() => open(featured.spine, `Spine — ${featured.titleCyr}`)}
            data-testid="typography-spine-trigger"
            className="relative block bg-black cursor-zoom-in group focus:outline-none overflow-hidden"
            aria-label="Open spine"
          >
            <img
              src={featured.spine}
              alt="Spine"
              className="block w-full h-auto transition-transform duration-[1200ms] ease-out group-hover:scale-[1.025]"
              loading="lazy"
            />
          </button>

          {/* BACK */}
          <button
            type="button"
            onClick={() => open(featured.back, `${featured.titleCyr} — back cover`)}
            data-testid="typography-back-trigger"
            className="relative block bg-black cursor-zoom-in group focus:outline-none overflow-hidden"
            aria-label="Open back cover"
          >
            <img
              src={featured.back}
              alt={`${featured.titleCyr} — back cover`}
              className="block w-full h-auto transition-transform duration-[1200ms] ease-out group-hover:scale-[1.025]"
              loading="lazy"
            />
            <span
              className="absolute top-4 right-4 font-mono text-[10px] uppercase tracking-[0.3em] text-white/90 bg-black/55 backdrop-blur-sm px-2.5 py-1"
              style={{ borderRight: `3px solid ${featured.accent}` }}
            >
              Front
            </span>
          </button>
        </motion.div>

        {/* Caption row underneath */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, delay: 0.15 }}
          className="mt-8 md:mt-12 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12"
        >
          <div className="lg:col-span-6">
            <h4 className="font-display italic text-3xl md:text-4xl leading-[1.05]">
              {featured.titleCyr}
            </h4>
            <div className="mt-3 font-sans-display uppercase tracking-[-0.01em] text-base md:text-lg text-white/70">
              {featured.title}
            </div>
            <div className="mt-5 flex flex-wrap items-center gap-3 text-xs uppercase tracking-[0.2em] text-white/55">
              <span>{featured.author}</span>
              <span className="w-1 h-1 rounded-full bg-white/30" />
              <span>{featured.publisher}</span>
            </div>
          </div>
          <p className="lg:col-span-6 text-base md:text-lg text-white/75 leading-relaxed max-w-[60ch]">
            {featured.blurb}
          </p>
        </motion.div>

        {/* Photo mockups */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, delay: 0.2 }}
          className="mt-10 md:mt-14"
        >
          <div className="flex items-center gap-4 mb-5 md:mb-6">
            <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/55">
              In context — photo mockups
            </span>
            <span className="flex-1 h-px bg-white/15" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-7 items-start">
            {mockups.map((mk, i) => (
              <button
                key={mk.src}
                type="button"
                onClick={() => open(mk.src, `${mk.label} — book mockup`)}
                data-testid={`typography-mockup-${i}`}
                className="group relative block overflow-hidden cursor-zoom-in focus:outline-none"
                aria-label={`Open ${mk.label} mockup`}
              >
                <div className="relative w-full aspect-[45/32] overflow-hidden">
                  <img
                    src={mk.src}
                    alt={`${mk.label} — book mockup`}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.025]"
                    loading="lazy"
                  />
                </div>
                <span
                  className="absolute top-4 left-4 font-mono text-[10px] uppercase tracking-[0.3em] text-white/90 bg-black/55 backdrop-blur-sm px-2.5 py-1"
                  style={{ borderLeft: `3px solid ${featured.accent}` }}
                >
                  Mockup · {String(i + 1).padStart(2, "0")}
                </span>
              </button>
            ))}
          </div>
        </motion.div>
      </div>

      {/* Magazine block — separate black panel after the book */}
      <div className="mt-8 md:mt-12 bg-[#0e0e0e] text-white p-6 md:p-10 lg:p-14">
        <div className="flex items-center justify-between mb-6 md:mb-8">
          <span
            className="font-mono text-[10px] uppercase tracking-[0.3em]"
            style={{ color: magazine.accent }}
          >
            {magazine.category}
          </span>
          <span className="hidden md:inline font-mono text-[10px] uppercase tracking-[0.3em] text-white/40">
            Click any spread to expand ↗
          </span>
        </div>

        {/* Featured flat spread */}
        <motion.button
          type="button"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.85 }}
          onClick={() => open(magazine.flat, `${magazine.title} — ${magazine.feature}, interior spread`)}
          data-testid="magazine-flat-trigger"
          className="block w-full overflow-hidden cursor-zoom-in group focus:outline-none shadow-[0_30px_80px_-20px_rgba(230,57,70,0.25)]"
          aria-label="Open magazine spread"
        >
          <div className="relative">
            <img
              src={magazine.flat}
              alt={`${magazine.title} — interior spread on Banksy`}
              className="block w-full h-auto transition-transform duration-[1200ms] ease-out group-hover:scale-[1.015]"
              loading="lazy"
            />
            <span
              className="absolute top-4 left-4 font-mono text-[10px] uppercase tracking-[0.3em] text-white bg-black/55 backdrop-blur-sm px-2.5 py-1"
              style={{ borderLeft: `3px solid ${magazine.accent}` }}
            >
              Spread · flat artwork
            </span>
          </div>
        </motion.button>

        {/* Caption */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, delay: 0.15 }}
          className="mt-8 md:mt-12 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12"
        >
          <div className="lg:col-span-6">
            <h4 className="font-display italic text-3xl md:text-4xl leading-[1.05]">
              {magazine.title}
            </h4>
            <div className="mt-3 font-sans-display uppercase tracking-[-0.01em] text-base md:text-lg text-white/70">
              {magazine.english}
            </div>
            <div className="mt-5 flex flex-wrap items-center gap-3 text-xs uppercase tracking-[0.2em] text-white/55">
              <span style={{ color: magazine.accent }}>
                {magazine.feature}
              </span>
              <span className="w-1 h-1 rounded-full bg-white/30" />
              <span>{magazine.year}</span>
            </div>
          </div>
          <p className="lg:col-span-6 text-base md:text-lg text-white/75 leading-relaxed max-w-[60ch]">
            {magazine.blurb}
          </p>
        </motion.div>

        {/* Magazine mockup */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, delay: 0.2 }}
          className="mt-10 md:mt-14"
        >
          <div className="flex items-center gap-4 mb-5 md:mb-6">
            <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/55">
              In context — photo mockup
            </span>
            <span className="flex-1 h-px bg-white/15" />
          </div>
          <button
            type="button"
            onClick={() => open(magazine.mockup, `${magazine.title} — mockup`)}
            data-testid="magazine-mockup-trigger"
            className="group relative block w-full overflow-hidden cursor-zoom-in focus:outline-none"
            aria-label="Open magazine mockup"
          >
            <img
              src={magazine.mockup}
              alt={`${magazine.title} — magazine mockup`}
              className="block w-full h-auto transition-transform duration-[1200ms] ease-out group-hover:scale-[1.02]"
              loading="lazy"
            />
            <span
              className="absolute top-4 left-4 font-mono text-[10px] uppercase tracking-[0.3em] text-white/90 bg-black/55 backdrop-blur-sm px-2.5 py-1"
              style={{ borderLeft: `3px solid ${magazine.accent}` }}
            >
              Mockup · 01
            </span>
          </button>
        </motion.div>
      </div>

      {/* Rasepasa dogs magazine block removed */}
    </div>
  );
}

function DogsMagazineBlock() {
  const { open } = useLightbox();
  const [idx, setIdx] = useState(0);
  const [dir, setDir] = useState(1);
  const page = dogsMag.pages[idx];
  const total = dogsMag.pages.length;
  const dragInfo = { startX: 0, startY: 0, didSwipe: false };

  const go = (delta) => {
    const next = (idx + delta + total) % total;
    setDir(delta);
    setIdx(next);
  };

  const onPointerDown = (e) => {
    dragInfo.startX = e.clientX;
    dragInfo.startY = e.clientY;
    dragInfo.didSwipe = false;
  };
  const onPointerUp = (e) => {
    const dx = e.clientX - dragInfo.startX;
    const dy = e.clientY - dragInfo.startY;
    if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy)) {
      dragInfo.didSwipe = true;
      go(dx < 0 ? 1 : -1);
    }
  };
  const onClick = () => {
    if (dragInfo.didSwipe) return;
    open(page.src, `${dogsMag.title} — ${page.label}`);
  };

  return (
    <div className="mt-8 md:mt-12 bg-[#0e0e0e] text-white p-6 md:p-10 lg:p-14" data-testid="dogs-magazine">
      <div className="flex items-center justify-between mb-6 md:mb-8">
        <span
          className="font-mono text-[10px] uppercase tracking-[0.3em]"
          style={{ color: dogsMag.accent }}
        >
          {dogsMag.category}
        </span>
        <span className="hidden md:inline font-mono text-[10px] uppercase tracking-[0.3em] text-white/40">
          Swipe pages · click image to expand ↗
        </span>
      </div>

      {/* Active page — draggable / swipeable */}
      <div className="relative">
        <div
          className={`relative mx-auto overflow-hidden ${page.portrait ? "max-w-[640px]" : "max-w-full"}`}
          style={{
            boxShadow: `0 30px 80px -20px ${dogsMag.accent}40`,
            touchAction: "pan-y",
          }}
        >
          <AnimatePresence custom={dir} mode="wait" initial={false}>
            <motion.div
              key={page.src}
              custom={dir}
              initial={{ opacity: 0, x: dir > 0 ? 80 : -80 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: dir > 0 ? -80 : 80 }}
              transition={{ duration: 0.4, ease: [0.65, 0.05, 0.3, 1] }}
              onPointerDown={onPointerDown}
              onPointerUp={onPointerUp}
              onClick={onClick}
              className="relative bg-black cursor-zoom-in select-none"
              data-testid="dogs-mag-active"
            >
              <img
                src={page.src}
                alt={`${dogsMag.title} — ${page.label}`}
                draggable={false}
                className="block w-full h-auto pointer-events-none"
                loading="lazy"
              />
              <span
                className="absolute top-4 left-4 font-mono text-[10px] uppercase tracking-[0.3em] text-white bg-black/55 backdrop-blur-sm px-2.5 py-1 pointer-events-none"
                style={{ borderLeft: `3px solid ${dogsMag.accent}` }}
              >
                {page.label}
              </span>
              <span className="absolute bottom-4 right-4 font-mono text-[10px] uppercase tracking-[0.3em] text-white/55 pointer-events-none">
                {idx + 1} / {total}
              </span>
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Prev / Next buttons */}
        <button
          type="button"
          onClick={() => go(-1)}
          aria-label="Previous page"
          data-testid="dogs-mag-prev"
          className="hidden md:flex absolute left-3 top-1/2 -translate-y-1/2 w-11 h-11 border border-white/40 bg-black/55 backdrop-blur-sm text-white items-center justify-center hover:bg-white hover:text-black transition-colors z-10"
        >
          <ArrowLeft className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={() => go(1)}
          aria-label="Next page"
          data-testid="dogs-mag-next"
          className="hidden md:flex absolute right-3 top-1/2 -translate-y-1/2 w-11 h-11 border border-white/40 bg-black/55 backdrop-blur-sm text-white items-center justify-center hover:bg-white hover:text-black transition-colors z-10"
        >
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>

      {/* Caption */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.7, delay: 0.15 }}
        className="mt-8 md:mt-12 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12"
      >
        <div className="lg:col-span-6">
          <h4 className="font-display italic text-3xl md:text-4xl leading-[1.05]">
            {dogsMag.title}
          </h4>
          <div className="mt-3 font-sans-display uppercase tracking-[-0.01em] text-base md:text-lg text-white/70">
            {dogsMag.english}
          </div>
          <div className="mt-5 flex flex-wrap items-center gap-3 text-xs uppercase tracking-[0.2em] text-white/55">
            <span style={{ color: dogsMag.accent }}>13-page issue</span>
            <span className="w-1 h-1 rounded-full bg-white/30" />
            <span>{dogsMag.year}</span>
          </div>
        </div>
        <p className="lg:col-span-6 text-base md:text-lg text-white/75 leading-relaxed max-w-[60ch]">
          {dogsMag.blurb}
        </p>
      </motion.div>

      {/* Page strip */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.7, delay: 0.2 }}
        className="mt-10 md:mt-14"
      >
        <div className="flex items-center gap-4 mb-5 md:mb-6">
          <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/55">
            All pages
          </span>
          <span className="flex-1 h-px bg-white/15" />
        </div>
        <div className="grid grid-cols-7 md:grid-cols-13 gap-2 md:gap-2.5" style={{ gridTemplateColumns: "repeat(13, minmax(0,1fr))" }}>
          {dogsMag.pages.map((p, i) => {
            const active = i === idx;
            return (
              <button
                key={p.src}
                type="button"
                onClick={() => {
                  setDir(i > idx ? 1 : -1);
                  setIdx(i);
                }}
                data-testid={`dogs-mag-thumb-${i}`}
                aria-label={`Show ${p.label}`}
                className="relative aspect-[3/4] bg-black overflow-hidden border transition-all"
                style={{
                  borderColor: active ? dogsMag.accent : "rgba(255,255,255,0.18)",
                  boxShadow: active ? `0 0 0 2px ${dogsMag.accent}` : "none",
                  transform: active ? "translateY(-2px)" : "none",
                }}
              >
                <img
                  src={p.src}
                  alt={p.label}
                  className="absolute inset-0 w-full h-full object-cover"
                  loading="lazy"
                />
              </button>
            );
          })}
        </div>
      </motion.div>
    </div>
  );
}
